import {
    createBillboardIconDataUri,
    createBillboardWithoutIconDataUri,
} from 'app/agriculture/icons/utils/map-icon-utils/createBillboardIconDataUri';
import { Position } from 'app/agriculture/queries/types/geometry/Position';
import { MapLocation } from 'app/agriculture/queries/types/MapLocation';
import { SelectionSubType, SelectionType } from 'app/shared/selection/SelectionType';
import { getAnimalIconConfig } from 'app/agriculture/icons/utils/map-icon-utils/getAnimalIconConfig';
import { AnimalSpecies, HerdType } from 'app/agriculture/models/entities/herd/HerdAnimals';
import polylabel from 'polylabel';
import * as turf from '@turf/helpers';
import destination from '@turf/destination';
import { Cartesian3, NearFarScalar, Color as CesiumColor } from 'cesium';
import { PolygonGeometry } from 'app/agriculture/queries/types/geometry/PolygonGeometry';

interface CreateMapIconOptions {
    isSelected: boolean;
    id: string;
    location: MapLocation;
}

const MAP_BILLBOARD_SCALE = 1;
const MAP_VISIBLE_ALPHA = 1.0;
const MAP_TRANSPARENT_ALPHA = 0.4;
const DegreesToMetersApprox = 1 / 111139;
const MapBillboardWidth = 72 * MAP_BILLBOARD_SCALE;
const MapBillboardHeight = 72 * MAP_BILLBOARD_SCALE;

const BillboardDefaults: Partial<Cesium.Billboard> = {
    width: MapBillboardWidth,
    show: true,
    height: MapBillboardHeight,
    scaleByDistance: new NearFarScalar(1e4, 1.0, 3e6, 0.0),
    disableDepthTestDistance: Number.POSITIVE_INFINITY,
    heightReference: 1, // Cesium.HeightReference.CLAMP_TO_GROUND,
    verticalOrigin: 1, // Cesium.VerticalOrigin.BOTTOM
};

export function createHerdIcon(
    createIconOptions: CreateMapIconOptions,
    herdOptions: {
        animalSpecies: AnimalSpecies;
        herdType?: HerdType | null;
        totalAnimals: number;
        isMoveIcon: boolean;
    },
): Partial<Cesium.Billboard> {
    const { icon, options } = getAnimalIconConfig(herdOptions);
    return {
        ...BillboardDefaults,
        verticalOrigin: 1, // clamp to bottom
        color: CesiumColor.WHITE.withAlpha(
            createIconOptions.isSelected ? MAP_VISIBLE_ALPHA : MAP_TRANSPARENT_ALPHA,
        ),
        id: {
            getId: () => createIconOptions.id,
            selectionSubType: SelectionSubType.HERD,
            selectionType: SelectionType.ENTITY,
        },
        position: Cartesian3.fromDegrees(
            createIconOptions.location.longitude,
            createIconOptions.location.latitude,
        ),
        image: createBillboardIconDataUri(icon, options),
    };
}

interface CreateIconOptions {
    isSelected: boolean;
    id: string | number;
    coordinates: Position;
    image: string;
    isPin: boolean;
}

export function createIcon({ id, coordinates, isSelected, image, isPin }: CreateIconOptions) {
    return {
        ...BillboardDefaults,
        verticalOrigin: isPin ? 1 : 0,
        color: CesiumColor.WHITE.withAlpha(isSelected ? MAP_VISIBLE_ALPHA : MAP_TRANSPARENT_ALPHA),
        id: {
            getId: () => id,
            selectionType: SelectionType.ENTITY,
        },
        position: Cesium.Cartesian3.fromDegrees(...coordinates),
        image,
    };
}

interface CreateMarkerOptions {
    isPin: boolean;
    location: MapLocation;
}

export function createMarker(createMarkerOptions: CreateMarkerOptions): Partial<Cesium.Billboard> {
    return {
        ...BillboardDefaults,
        verticalOrigin: createMarkerOptions.isPin ? 1 : 0,
        id: 'marker',
        position: Cartesian3.fromDegrees(
            createMarkerOptions.location.longitude,
            createMarkerOptions.location.latitude,
        ),
        image: createBillboardWithoutIconDataUri({
            billboardType: createMarkerOptions.isPin ? 'pin' : 'circle',
        }),
    };
}

export function getPositionNearCenter(
    totalHerds: number,
    indexOfHerd: number,
    polygon: PolygonGeometry,
) {
    const location = polylabel(polygon.coordinates, DegreesToMetersApprox);
    const [longitude, latitude] = location;
    if (longitude == null || latitude == null) return null;
    const radius = location.distance * (1 / DegreesToMetersApprox) * 0.66;
    const angle = totalHerds == 0 ? 0 : (360 / totalHerds) * indexOfHerd;
    const point = turf.point([longitude, latitude]);
    const result = destination(point, radius, angle, {
        units: 'meters',
    });
    return result.geometry.coordinates as Position;
}
